import { preloadPostOfficeConfluenceSideNav } from '@confluence/experiment-post-office-side-nav/entry-points/preloadConfluenceSideNav.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList.ts';
import { FORGE_MODULE_SPACE_PAGE } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { preloadWebItemLocation } from '@confluence/web-item-location/entry-points/preloadWebItemLocation.ts';
import {
	SYSTEM_HEADER_LEFT,
	SYSTEM_USER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { preloadPersistentInviteButtonEligible } from '@confluence/persistent-invite-button/entry-points/preloadPersistentInviteEligible.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import {
	expVal,
	expValEquals,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { preloadSuggestionCount } from '@confluence/space-curation/entry-points/preloadSpaceCuration.ts';
import { fg } from '@confluence/feature-gating';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { preloadShortcutsSmartLinks } from '@confluence/space-shortcuts/entry-points/preloadShortcutsSmartLinks.ts';
import { preloadFooterCreateButton } from '@confluence/create-blank-fabric-page/entry-points/preloadFooterCreateButton.ts';
import { preloadIsUserViewingSpaceAsAnon } from '@confluence/anonymous-access/entry-points/preloadIsUserViewingSpaceAsAnon.ts';

import { preloadPlatformNavigationQuery } from './preloadPlatformNavigationQuery.ts';
import { preloadInvitedAppShortcutsRecommendationsQuery } from './preloadInvitedAppShortcutsRecommendationsQuery.ts';
import SpaceNavigationQuery, {
	type SpaceNavigationQuery$data,
	type SpaceNavigationQuery$variables,
} from './SpaceNavigation/__generated__/SpaceNavigationQuery.graphql.ts';

type NavigationPreloadVariables = Pick<
	SpaceNavigationQuery$variables,
	| 'contentId'
	| 'includeBlogTree'
	| 'includeHomepageTreeVariant'
	| 'includePageTree'
	| 'isSuperAdmin'
	| 'paginationLimit'
	| 'ptStatuses'
>;

// Dynamic config for the space curation preload timeout; tunable without a deploy.
const SPACE_CURATION_PRELOAD_TIMEOUT_CONFIG = 'confluence_space_curation_preload_timeout';

export async function preloadSideNavigation(
	spaceKey: string,
	previousSpaceKey?: string,
	navigationVariables?: NavigationPreloadVariables,
): Promise<void | any[] | Error[]> {
	const preloadedSessionContext = getPreloaderFnContext();
	const { cloudId, edition, isLicensed, userId, accessStatus } = preloadedSessionContext;

	// Only preload space-curation data on SSR or when navigating to a different space.
	// Within-space navigation (page-to-page) can reuse the Apollo cache.
	const shouldPreloadSpaceCuration =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		Boolean(process.env.REACT_SSR) || previousSpaceKey !== spaceKey;
	const hasLicensedAccess =
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS;
	const includePersistentInviteLicense =
		edition !== ConfluenceEdition.FREE &&
		Boolean(userId) &&
		(!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_license_error_fix')
			? hasLicensedAccess
			: isLicensed);
	const shouldPreloadSuggestionCount =
		shouldPreloadSpaceCuration &&
		UNSAFE_expValEqualsNoExposure('confluence_autonomous_space_curation', 'isEnabled', true);

	const tasks: Array<Promise<any>> = [];

	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	if (fg('navx-global-side-nav-confluence')) {
		tasks.push(preloadPlatformNavigationQuery());
		// Self-gates on the kill-switch gate + experiment; no-ops for non-cohort users.
		tasks.push(preloadInvitedAppShortcutsRecommendationsQuery());
	}

	const routeQueryResult = query<SpaceNavigationQuery$data, SpaceNavigationQuery$variables>({
		query: SpaceNavigationQuery,
		variables: {
			cloudId,
			spaceKey,
			isLicensed,
			includePersistentInviteLicense,
			includeSpaceViewsPersistence: preloadedSessionContext.isLicensed,
			includeSharedSpaceDetails:
				!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_shared_space_frag_reader') &&
				expVal('confluence_exp_space_nav_contract', 'isEnabled', false),
			includeSpaceCreationEligibility:
				!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_creation_frag_reader') &&
				expVal('confluence_exp_space_nav_contract', 'isEnabled', false),
			...navigationVariables,
		},
	});
	tasks.push(routeQueryResult);

	if (
		navigationVariables &&
		!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_core_frag_reader') &&
		expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
	) {
		tasks.push(preloadShortcutsSmartLinks(spaceKey));
	}

	if (
		navigationVariables &&
		!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_trees_frag_reader') &&
		expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
	) {
		if (
			StatsigConfigurations.isKillSwitchOn('confluence_space_nav_creation_frag_reader') ||
			!expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
		) {
			tasks.push(preloadFooterCreateButton(spaceKey));
		}
	}

	if (
		navigationVariables &&
		!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_affordances_frag_reader') &&
		expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
	) {
		tasks.push(
			routeQueryResult
				.then((result) =>
					result.data?.space ? Promise.resolve() : preloadIsUserViewingSpaceAsAnon(spaceKey),
				)
				.catch(() => preloadIsUserViewingSpaceAsAnon(spaceKey)),
		);
	}

	tasks.push(
		preloadUseExtensionList({
			spaceKey,
			moduleType: FORGE_MODULE_SPACE_PAGE,
			spaceContextOnly: true,
		}),
	);

	if (
		StatsigConfigurations.isKillSwitchOn('confluence_space_nav_affordances_frag_reader') ||
		!expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
	) {
		tasks.push(preloadPersistentInviteButtonEligible({ spaceKey }, false));
	}

	if (expValEquals('web_item_query_batch_system_call', 'isEnabled', true)) {
		// Keep distinct cache keys per location and let the transport batch the requests together.
		tasks.push(
			preloadWebItemLocation({
				location: SYSTEM_HEADER_LEFT,
				context: { single: false },
			}),
			preloadWebItemLocation({
				location: SYSTEM_USER,
				context: { single: false },
			}),
		);
	} else {
		// Fallback: Original behavior - only preload SYSTEM_HEADER_LEFT separately
		tasks.push(
			preloadWebItemLocation({
				location: SYSTEM_HEADER_LEFT,
			}),
		);
	}

	tasks.push(preloadPostOfficeConfluenceSideNav());

	if (shouldPreloadSuggestionCount) {
		tasks.push(
			createDataFetcherForSuspense('space-curation', () => [
				// Guard inside the callback so it wraps the real query (an outer guard no-ops on the
				// SSR-streaming path).
				slowQueryGuard(
					'preloadSpaceCuration',
					() => waitForAllReturnWhenError([preloadSuggestionCount(spaceKey, cloudId)]),
					StatsigConfigurations.getNumber(
						SPACE_CURATION_PRELOAD_TIMEOUT_CONFIG,
						SLOW_QUERY_DEFAULT_TIMEOUT,
					),
				),
			]),
		);
	}

	return waitForAllReturnWhenError(tasks);
}
